import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import {
  validateProjectInput,
  type Project,
  type ProjectInput,
  type ProjectSummary,
  type ProjectType,
} from "../../domain/project";

interface ProjectRow {
  id: string;
  name: string;
  job_number: string | null;
  project_type: string;
  status_id: string;
  street_address: string;
  city: string;
  state: string;
  zip: string;
  purchase_price_cents: number | null;
  expected_arv_cents: number | null;
  contingency_cents: number | null;
  estimating_tax_pct_x100: number | null;
  sourcing_zip: string | null;
  sourcing_store_id: string | null;
  strategy?: string | null;
  living_area_sqft_x100?: number | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

interface ProjectSummaryRow extends ProjectRow {
  status_display_name: string;
}

/** Thrown when input fails validation; carries user-readable problems. */
export class ValidationError extends Error {
  constructor(public readonly problems: string[]) {
    super(problems.join(" "));
    this.name = "ValidationError";
  }
}

function mapProject(row: ProjectRow): Project {
  return {
    id: row.id,
    name: row.name,
    jobNumber: row.job_number ?? "",
    projectType: (row.project_type ?? "renovation") as ProjectType,
    statusId: row.status_id,
    streetAddress: row.street_address,
    city: row.city,
    state: row.state,
    zip: row.zip,
    purchasePriceCents: row.purchase_price_cents === null ? null : Number(row.purchase_price_cents),
    expectedArvCents: row.expected_arv_cents === null ? null : Number(row.expected_arv_cents),
    contingencyCents: row.contingency_cents === null ? null : Number(row.contingency_cents),
    estimatingTaxPctX100:
      row.estimating_tax_pct_x100 === null ? null : Number(row.estimating_tax_pct_x100),
    sourcingZip: row.sourcing_zip ?? null,
    sourcingStoreId: row.sourcing_store_id ?? null,
    strategy: row.strategy === "rental" ? "rental" : "flip",
    livingAreaSqftX100:
      row.living_area_sqft_x100 === null || row.living_area_sqft_x100 === undefined ? null : Number(row.living_area_sqft_x100),
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export class ProjectRepository {
  constructor(private readonly db: DbExecutor) {}

  /**
   * Allocate the next immutable Job Number (spec §12B) inside the CURRENT
   * transaction: per-(org, year) sequence row, incremented and read back
   * atomically — never MAX()+1, safe under concurrent SaaS use. Format
   * YY + 3-digit zero-padded sequence, expanding naturally past 999.
   */
  private async allocateJobNumber(now: string): Promise<string> {
    const year = Number(now.slice(0, 4));
    await this.db.execute("INSERT OR IGNORE INTO job_number_sequences (org_id, year, next_seq) VALUES ('default', ?, 0)", [year]);
    await this.db.execute("UPDATE job_number_sequences SET next_seq = next_seq + 1 WHERE org_id = 'default' AND year = ?", [year]);
    const rows = await this.db.select<{ next_seq: number }>(
      "SELECT next_seq FROM job_number_sequences WHERE org_id = 'default' AND year = ?",
      [year],
    );
    const seq = Number(rows[0].next_seq);
    return String(year).slice(2) + String(seq).padStart(3, "0");
  }

  async createProject(input: ProjectInput): Promise<Project> {
    const problems = validateProjectInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    const now = new Date().toISOString();
    const project: Project = {
      id: generateId(),
      name: input.name.trim(),
      jobNumber: "",
      projectType: input.projectType ?? "renovation",
      statusId: input.statusId,
      streetAddress: input.streetAddress.trim(),
      city: input.city.trim(),
      state: input.state.trim(),
      zip: input.zip.trim(),
      purchasePriceCents: input.purchasePriceCents,
      expectedArvCents: input.expectedArvCents,
      contingencyCents: null,
      estimatingTaxPctX100: null,
      sourcingZip: null,
      sourcingStoreId: null,
      strategy: input.strategy ?? "flip",
      livingAreaSqftX100: input.livingAreaSqftX100 ?? null,
      notes: input.notes,
      createdAt: now,
      updatedAt: now,
    };
    await this.db.transaction(async () => {
      project.jobNumber = await this.allocateJobNumber(now);
      await this.db.execute(
        `INSERT INTO projects
           (id, name, job_number, project_type, status_id, street_address, city, state, zip,
            purchase_price_cents, expected_arv_cents, notes, created_at, updated_at,
            strategy, living_area_sqft_x100)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          project.id,
          project.name,
          project.jobNumber,
          project.projectType,
          project.statusId,
          project.streetAddress,
          project.city,
          project.state,
          project.zip,
          project.purchasePriceCents,
          project.expectedArvCents,
          project.notes,
          project.createdAt,
          project.updatedAt,
          project.strategy,
          project.livingAreaSqftX100,
        ],
      );
    });
    return project;
  }

  async updateProject(id: string, input: ProjectInput): Promise<Project> {
    const problems = validateProjectInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    const existing = await this.getProject(id);
    if (!existing) {
      throw new Error(`Project ${id} not found.`);
    }
    const updatedAt = new Date().toISOString();
    await this.db.execute(
      `UPDATE projects SET
         name = ?, project_type = COALESCE(?, project_type), status_id = ?, street_address = ?, city = ?, state = ?, zip = ?,
         purchase_price_cents = ?, expected_arv_cents = ?, notes = ?, updated_at = ?,
         strategy = COALESCE(?, strategy),
         living_area_sqft_x100 = CASE WHEN ? = 1 THEN ? ELSE living_area_sqft_x100 END
       WHERE id = ?`,
      [
        input.name.trim(),
        input.projectType ?? null,
        input.statusId,
        input.streetAddress.trim(),
        input.city.trim(),
        input.state.trim(),
        input.zip.trim(),
        input.purchasePriceCents,
        input.expectedArvCents,
        input.notes,
        updatedAt,
        input.strategy ?? null,
        input.livingAreaSqftX100 === undefined ? 0 : 1,
        input.livingAreaSqftX100 ?? null,
        id,
      ],
    );
    const updated = await this.getProject(id);
    if (!updated) {
      throw new Error(`Project ${id} disappeared during update.`);
    }
    return updated;
  }

  async getProject(id: string): Promise<Project | null> {
    const rows = await this.db.select<ProjectRow>(
      `SELECT id, name, job_number, project_type, status_id, street_address, city, state, zip,
              purchase_price_cents, expected_arv_cents, contingency_cents,
              estimating_tax_pct_x100, sourcing_zip, sourcing_store_id, strategy, living_area_sqft_x100,
              notes, created_at, updated_at
         FROM projects WHERE id = ?`,
      [id],
    );
    return rows.length > 0 ? mapProject(rows[0]) : null;
  }

  /** Set the estimating tax assumption (hundredths of a percent; null = all-in prices). */
  async setEstimatingTaxRate(id: string, pctX100: number | null): Promise<void> {
    if (pctX100 !== null && (!Number.isSafeInteger(pctX100) || pctX100 < 0)) {
      throw new ValidationError(["Estimating tax rate must be a valid non-negative percent."]);
    }
    await this.db.execute(
      "UPDATE projects SET estimating_tax_pct_x100 = ?, updated_at = ? WHERE id = ?",
      [pctX100, new Date().toISOString(), id],
    );
  }

  /** Sourcing market override (spec 26A). Null/blank = project address ZIP. */
  async setSourcingZip(id: string, zip: string | null): Promise<void> {
    const value = zip === null || zip.trim() === "" ? null : zip.trim();
    await this.db.execute(
      "UPDATE projects SET sourcing_zip = ?, updated_at = ? WHERE id = ?",
      [value, new Date().toISOString(), id],
    );
  }

  /** Home Depot store this project prices against (null = resolve from ZIP). */
  async setSourcingStoreId(id: string, storeId: string | null): Promise<void> {
    const value = storeId === null || storeId.trim() === "" ? null : storeId.trim();
    await this.db.execute(
      "UPDATE projects SET sourcing_store_id = ?, updated_at = ? WHERE id = ?",
      [value, new Date().toISOString(), id],
    );
  }

  /** Set the project's rehab contingency buffer (null = not entered). */
  async setContingency(id: string, contingencyCents: number | null): Promise<void> {
    if (contingencyCents !== null && !Number.isSafeInteger(contingencyCents)) {
      throw new ValidationError(["Contingency must be a valid dollar amount."]);
    }
    await this.db.execute(
      "UPDATE projects SET contingency_cents = ?, updated_at = ? WHERE id = ?",
      [contingencyCents, new Date().toISOString(), id],
    );
  }

  async listProjects(): Promise<ProjectSummary[]> {
    const rows = await this.db.select<ProjectSummaryRow>(
      `SELECT p.id, p.name, p.job_number, p.project_type, p.status_id, p.street_address, p.city, p.state, p.zip,
              p.purchase_price_cents, p.expected_arv_cents, p.contingency_cents,
              p.estimating_tax_pct_x100, p.sourcing_zip, p.sourcing_store_id, p.strategy, p.living_area_sqft_x100,
              p.notes, p.created_at, p.updated_at,
              s.display_name AS status_display_name
         FROM projects p
         JOIN project_statuses s ON s.id = p.status_id
        ORDER BY p.created_at DESC`,
    );
    return rows.map((row) => ({
      ...mapProject(row),
      statusDisplayName: row.status_display_name,
    }));
  }
}
